import { useState, type ClipboardEvent } from 'react';
import { PASTE_ERRORS, gridToText, type PasteResult, type PasteSource } from '../../core/paste';
import type { Rubric } from '../../core/rubric';
import { gridFromHtml } from '../paste';

export interface PasteRubricProps {
  /** The built rubric goes to the editor for review; nothing is saved here. */
  onReview: (rubric: Rubric) => void;
  onCancel: () => void;
  onParsed?: (source: PasteSource, result: PasteResult) => void;
  /** AI structuring. Resolves to a result or a message to show. */
  onStructure: (text: string) => Promise<PasteResult | string>;
}

/** Build with AI: paste a rubric in any format, AI turns it into criteria and levels. */
export default function PasteRubric({ onReview, onCancel, onParsed, onStructure }: PasteRubricProps) {
  const [text, setText] = useState('');
  const [result, setResult] = useState<PasteResult | null>(null);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState('');

  const build = async () => {
    setBuilding(true); setError(''); setResult(null);
    const outcome = await onStructure(text);
    setBuilding(false);
    if (typeof outcome === 'string') { setError(outcome); return; }
    setResult(outcome);
    onParsed?.('ai', outcome);
  };

  // A table copied from a doc keeps its columns as tabs, which helps AI read it.
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const grid = gridFromHtml(event.clipboardData.getData('text/html'));
    if (!grid) return;
    event.preventDefault();
    setText(gridToText(grid)); setResult(null);
  };

  const levels = result?.ok ? result.rubric.items[0]!.levels.map(l => l.name) : [];

  return (
    <div className="paste-rubric">
      <h3>Build with AI <span className="ai-badge" aria-hidden="true">AI</span></h3>
      <p className="hint">Paste your rubric in any format — a table, a list, or paragraphs. AI turns it into criteria and levels, and you review everything before it is saved. Only the rubric text is sent to AI.</p>
      <textarea className="ph-mask" aria-label="Your rubric" rows={6} value={text} autoFocus disabled={building}
        placeholder="Paste your rubric here."
        onPaste={onPaste} onChange={e => { setText(e.target.value); setResult(null); setError(''); }} />
      {result && !result.ok && <p className="message" data-tone="error" role="alert">{PASTE_ERRORS[result.reason]}</p>}
      {error && <p className="message" data-tone="error" role="alert">{error}</p>}
      {result?.ok && (
        <div className="paste-preview" role="status">
          <p><strong>{result.rubric.items.length} {result.rubric.items.length === 1 ? 'criterion' : 'criteria'}</strong> · {levels.length} levels, low to high: {levels.join(' → ')}</p>
          <ul className="ph-mask">{result.rubric.items.map(it => <li key={it.id}>{it.name}</li>)}</ul>
          {result.warnings.map(w => <p key={w} className="hint">{w}</p>)}
        </div>
      )}
      <div className="pane-actions">
        <button type="button" className="secondary" onClick={onCancel}>Cancel</button>
        {result?.ok
          ? <button type="button" className="primary" onClick={() => onReview(result.rubric)}>Review and save</button>
          : <button type="button" className="primary" disabled={!text.trim() || building} aria-busy={building} onClick={build}>{building ? 'Reading your rubric…' : 'Build rubric'}</button>}
      </div>
    </div>
  );
}
